import { useQuery } from '@tanstack/react-query';
import { ResourceRepositoryImpl } from '../../data/repository/resource_repository_impl';
import { useMemo } from 'react';
import useWindowSize from '../../../../core/utils/use_windows_size';

const BASE_PADDING_PERCENT = 0.3 * 2;
const LG_PADDING_PERCENT = 0.1 * 2;
const GAP_SIZE = 12;
const RESOURCE_ELEMENT_WIDTH = 130;

export const useGetResourceByThemeId = (themeId?: string, limit: boolean = true) => {
  const repository = new ResourceRepositoryImpl();
  const { width: screenWidth } = useWindowSize();

  const queryResult = useQuery({
    queryFn: () => repository.getResourceByThemeId(themeId!),
    queryKey: ['resourceByThemeId', themeId],
    enabled: !!themeId,
  });

  const calculateRequiredItems = useMemo(() => {
    const isLarge = screenWidth >= 1024;
    const horizontalPadding = isLarge 
      ? BASE_PADDING_PERCENT * screenWidth 
      : LG_PADDING_PERCENT * screenWidth;
      
    const availableWidth = screenWidth - horizontalPadding;
      
    const itemsCount = Math.floor((availableWidth + GAP_SIZE) / (RESOURCE_ELEMENT_WIDTH + GAP_SIZE));
      
    return Math.max(itemsCount + 3, 10);
  }, [screenWidth]);

  const filteredVideos = useMemo(() => queryResult.data?.filter(resource => resource.type === 'video') || [], [queryResult.data]);
  const videos = useMemo(() => {
    return limit ? filteredVideos.slice(0, calculateRequiredItems) : filteredVideos;
  }, [filteredVideos, calculateRequiredItems, limit]);
  const hasMoreVideos = useMemo(() => {
    return filteredVideos.length > calculateRequiredItems;
  }, [filteredVideos, calculateRequiredItems]);

  const filteredArticles = useMemo(() => queryResult.data?.filter(resource => resource.type === 'article') || [], [queryResult.data]);
  const articles = useMemo(() => {
    return limit ? filteredArticles.slice(0, calculateRequiredItems) : filteredArticles;
  }, [filteredArticles, calculateRequiredItems, limit]);
  const hasMoreArticles = useMemo(() => {
    return filteredArticles.length > calculateRequiredItems;
  }, [filteredArticles, calculateRequiredItems]);


  const filteredAudios = useMemo(() => queryResult.data?.filter(resource => resource.type === 'audio') || [], [queryResult.data]);
  const audios = useMemo(() => {
    return limit ? filteredAudios.slice(0, calculateRequiredItems) : filteredAudios;
  }, [filteredAudios, calculateRequiredItems, limit]);
  const hasMoreAudios = useMemo(() => {
    return filteredAudios.length > calculateRequiredItems;
  }, [filteredAudios, calculateRequiredItems]);

  return {
    ...queryResult,
    videos,
    articles,
    audios,
    hasMoreVideos,
    hasMoreArticles,
    hasMoreAudios,
  };
}
